import React, { useMemo, useState } from 'react';
import CarteAffectation from '../AffectationCarte/CarteAffectation';
import ViewSwitch from '../ViewSwitch/ViewSwitch';
import './PrestataireLotMap.css';

const fmtDeptCode = (id: number) => (id < 100 ? String(id).padStart(2, '0') : String(id));

// Deux etats sur la MEME carte que la gestion d'equipe : titulaire de l'accord-cadre (orange brule)
// vs intervention libre (orange primaire). Palette fermee du design system.
const LIBRE_ID = 1;
const TITULAIRE_ID = 2;
const COULEUR_LIBRE = '#f97316';
const COULEUR_TITULAIRE = '#c2410c';
const COULEURS: Map<number, string> = new Map([[LIBRE_ID, COULEUR_LIBRE], [TITULAIRE_ID, COULEUR_TITULAIRE]]);
const NOMS: Map<number, string> = new Map([[LIBRE_ID, 'Intervention libre'], [TITULAIRE_ID, 'Titulaire (accord-cadre)']]);
const EMPTY_SELECTION: Set<number> = new Set();
const NOOP = (): void => undefined;

interface LotTech {
  id: number;
  nom: string;
  code: string;
  is_ac?: boolean;
}
interface LotDept {
  lot_technique_id: number;
  departement_id?: number | null;
  departement_nom?: string;
  region_id?: number | null;
  // true = titulaire de l'accord-cadre sur ce departement ; false = intervention libre.
  is_locked?: boolean;
}

interface PrestataireLotMapProps {
  lotsTechniques: LotTech[];
  lotsDepartements: LotDept[];
}

/**
 * Perimetre geographique d'intervention d'un prestataire par lot technique (modale annuaire,
 * lecture seule). Select du lot + bascule Carte / Liste. La carte est la meme que la gestion
 * d'equipe et DISTINGUE les zones titulaires de l'accord-cadre (orange brule) des zones
 * d'intervention libre (orange primaire).
 */
const PrestataireLotMap: React.FC<PrestataireLotMapProps> = ({ lotsTechniques, lotsDepartements }) => {
  const [selectedLot, setSelectedLot] = useState<number | null>(lotsTechniques[0]?.id ?? null);
  const [vue, setVue] = useState<'carte' | 'liste'>('carte');

  // Departements couverts (id + nom + titulaire ?) pour le lot choisi. Titulaire prime sur libre
  // en cas de doublon (un departement titulaire est couvert par l'AC, pas en libre).
  const coveredDepts = useMemo(() => {
    const m = new Map<number, { nom: string; titulaire: boolean }>();
    lotsDepartements
      .filter((ld) => ld.lot_technique_id === selectedLot && ld.departement_id != null)
      .forEach((ld) => {
        const id = ld.departement_id as number;
        const prev = m.get(id);
        m.set(id, {
          nom: ld.departement_nom ?? prev?.nom ?? '',
          titulaire: (prev?.titulaire ?? false) || ld.is_locked === true,
        });
      });
    return Array.from(m.entries())
      .map(([id, v]) => ({ id, nom: v.nom, titulaire: v.titulaire }))
      .sort((a, b) => a.id - b.id);
  }, [selectedLot, lotsDepartements]);

  // Couvert -> "affecte" titulaire ou libre ; les autres departements restent hors map (gris fonce).
  const affecteParDept = useMemo(() => {
    const m = new Map<number, number | null>();
    coveredDepts.forEach((d) => m.set(d.id, d.titulaire ? TITULAIRE_ID : LIBRE_ID));
    return m;
  }, [coveredDepts]);

  if (lotsTechniques.length === 0) return null;

  const count = coveredDepts.length;
  const nbTitulaire = coveredDepts.filter((d) => d.titulaire).length;
  const nbLibre = count - nbTitulaire;

  return (
    <div className="prestataire-lot-map">
      <div className="prestataire-lot-map-bar">
        <label className="prestataire-lot-field">
          <span>Lot technique</span>
          <select
            value={selectedLot ?? ''}
            onChange={(e) => setSelectedLot(Number(e.target.value))}
          >
            {lotsTechniques.map((lt) => (
              <option key={lt.id} value={lt.id}>
                {lt.code} - {lt.nom}{lt.is_ac ? ' (AC)' : ''}
              </option>
            ))}
          </select>
        </label>
        <ViewSwitch
          value={vue}
          options={[{ value: 'carte', label: 'Carte' }, { value: 'liste', label: 'Liste' }]}
          onChange={setVue}
          ariaLabel="Affichage"
        />
      </div>
      <span className="prestataire-lot-map-count">
        {count} département{count > 1 ? 's' : ''} couvert{count > 1 ? 's' : ''}
        {count > 0 && (nbTitulaire > 0 && nbLibre > 0
          ? ` · ${nbTitulaire} titulaire${nbTitulaire > 1 ? 's' : ''}, ${nbLibre} en libre`
          : nbTitulaire > 0 ? ' · titulaire de l’accord-cadre' : ' · intervention libre')}
      </span>

      {vue === 'carte' ? (
        <div className="prestataire-lot-mapcard">
          <CarteAffectation
            affecteParDept={affecteParDept}
            couleurParAffecte={COULEURS}
            nomParAffecte={NOMS}
            selection={EMPTY_SELECTION}
            onToggle={NOOP}
            readOnly
          />
        </div>
      ) : count === 0 ? (
        <p className="readonly-empty">Aucun département couvert.</p>
      ) : (
        <div className="prestataire-lot-list">
          {coveredDepts.map((d) => (
            <div key={d.id} className="prestataire-lot-list-row">
              <span>{fmtDeptCode(d.id)} - {d.nom}</span>
              <span className={`prestataire-lot-tag ${d.titulaire ? 'is-titulaire' : 'is-libre'}`}>
                {d.titulaire ? 'Titulaire' : 'Libre'}
              </span>
            </div>
          ))}
        </div>
      )}

      {count > 0 && (
        <div className="prestataire-lot-legende">
          <span className="prestataire-lot-legende-item">
            <span className="prestataire-lot-pastille" style={{ background: COULEUR_LIBRE }} />
            Intervention libre
          </span>
          <span className="prestataire-lot-legende-item">
            <span className="prestataire-lot-pastille" style={{ background: COULEUR_TITULAIRE }} />
            Titulaire de l’accord-cadre
          </span>
        </div>
      )}
    </div>
  );
};

export default PrestataireLotMap;
